"use client"

import { motion } from "framer-motion"
import { useState } from "react"
import "../stylesheets/Valentines.css"

const cardTexts = [
    "Power", "Ow", "Smith", "Shakespeare",
    "Germany", "Paper", "Tsunami", "Juvia",
    "Thunder", "Earthquake", "Solo", "Rose",
    "Vitamin", "Rock", "Salt", "Iron"
  ]

const initialCards = Array.from({ length: 16 }, (_, i) => ({
  id: i + 1,
  label: `${cardTexts[i]}`,
}))

export default function AnimatedGrid() {
  const shuffle = (array) => [...array].sort(() => Math.random() - 0.5)
  const [cards, setCards] = useState(() => shuffle(initialCards))
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [isShaking, setIsShaking] = useState(false)
  const [won, setWon] = useState(false)
  const [statusText, setStatusText] = useState("Select 4 cards to group & submit")
  const [groupedCards, setGroupedCards] = useState([])

  const selectedCount = selectedIds.size

  const groupMatchings = {
    0:  0, 1:  0, 2:  0, 3:  0, // Group 1
    4:  1, 5:  1, 6:  1, 7:  1, // Group 2
    8:  2, 9:  2, 10: 2, 11: 2, // Group 3
    12: 3, 13: 3, 14: 3, 15: 3, // Group 4
  }

  const groupNames = [
    ["Starts with ", "Will", ": Power 🏐, Ow, Smith, Shakespeare"],
    ["From your ", "You", "th", ": Germany, Paper, Tsunami, Juvia"],
    ["B", "lackPink", ": Thunder, Earthquake, Solo, Rose",],
    ["Mine", "rals", ": Vitamin, Rock 💍, Salt, Iron",],
  ]

  const groupHighlightIndices = [1, 1, 0, 0]

  const groupColors = ["red", "red", "red", "red"]

const shuffleCards = () => {
    setCards(prev => {
        const groupedSet = new Set(groupedCards)
        const ungroupedCards = prev.filter(card => !groupedSet.has(card.id))
        const shuffledUngrouped = [...ungroupedCards].sort(() => Math.random() - 0.5)
        const grouped = prev.filter(card => groupedSet.has(card.id))
        return [...grouped, ...shuffledUngrouped]
    })
}

  const handleCardClick = (card) => {
    if (groupedCards.includes(card.id)) return // Ignore clicks on already grouped cards
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(card.id)) {
        next.delete(card.id)
      } else {
        next.add(card.id)
      }
      return next
    })
  }

  const handleSubmit = () => {
    console.log("Submit clicked")
    console.log("Selected count:", selectedCount)

    // 👉 put game logic here (validate, reorder, etc.)
    const selectedGroupIds = Array.from(selectedIds).map(id => groupMatchings[id - 1])
    console.log(selectedIds, selectedGroupIds)
    const isSameGroup = selectedGroupIds.every(groupId => groupId === selectedGroupIds[0])

    if (isSameGroup) {
        setGroupedCards(prev => [...prev, ...Array.from(selectedIds)])
        setSelectedIds(new Set())
        setStatusText("Correct! Keep going...")

        setCards(prev => {
            const groupedSet = new Set([...groupedCards, ...Array.from(selectedIds)])
            const grouped = prev.filter(card => groupedSet.has(card.id))
                .sort((a, b) => (groupMatchings[a.id - 1] * 4 + (a.id - 1) % 4) - (groupMatchings[b.id - 1] * 4 + (b.id - 1) % 4))
            const ungrouped = prev.filter(card => !groupedSet.has(card.id))
            return [...grouped, ...ungrouped]
        })

        if (groupedCards.length + 4 === cards.length) {
            setWon(true)
            setStatusText("Congratulations, YOU WON!")
        }
    } else {
        // Shake all cards for 1 second
        setIsShaking(true)
        setTimeout(() => setIsShaking(false), 1700)
        setStatusText("Wrong...")
    }
  }

    return (
        <div className="page">
            <h1 className="title">❤️ Valentine&apos;s Day Puzzle ❤️</h1>

            {!won ? (
                <>
                    <div className="grid">
                        {cards.map(card => (
                            <motion.div
                                key={card.id}
                                layout
                                onClick={() => handleCardClick(card)}
                                className={`card ${selectedIds.has(card.id) ? "selected" : ""}`}
                                style={{
                                    border: groupedCards.includes(card.id) ? `3px solid ${groupColors[groupMatchings[card.id - 1]]}` : undefined,
                                }}
                                transition={{ type: "spring", stiffness: 260, damping: 28 }}
                                initial={{ opacity: 0 }}
                                animate={
                                    isShaking
                                        ? { x: [-8, 0, 8, 0], opacity: 1 }
                                        : { x: 0, opacity: 1 }
                                }
                                whileHover={{ scale: 1.04 }}
                                whileTap={{ scale: 0.96 }}
                            >
                                {card.label}
                            </motion.div>
                        ))}
                    </div>

                </>
            ) : (
                <div className="rows">
                    {Array.from({ length: 4 }, (_, rowIndex) => (
                        <motion.div
                            key={rowIndex}
                            className="row"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 5 }}
                            style={{ 
                                border: `3px solid ${groupColors[rowIndex]}` 
                            }}
                        >
                           <h2>
                            {groupNames[rowIndex].map((part, partIndex) => (
                                <span
                                key={partIndex}
                                className={
                                    partIndex === groupHighlightIndices[rowIndex]
                                    ? "highlight"
                                    : (
                                        partIndex === groupNames[rowIndex].length - 1 ? "small" : ""
                                    )
                                }
                                >
                                {part}
                                </span>
                            ))}
                            </h2>
                        </motion.div>
                    ))}
                </div>
            )}
            <div className="buttonRow">
                {!won ? 
                <>
                    <button onClick={shuffleCards}>Shuffle</button>
                    <button
                        onClick={selectedCount === 4 ? handleSubmit : undefined}
                        style={{
                            backgroundColor: selectedCount === 4 ? "var(--lightred)" : "#ffb6b6",
                            pointerEvents: selectedCount === 4 ? "auto" : "none",
                        }}
                    >
                        Submit
                    </button>
                </> : <>
                    <h4 style={{ fontSize: "24px" }}>Answer:</h4>
                    <button
                        onClick={() => {
                            window.location.href =
                            "mailto:mrmackamoo@gmail.com?subject=My Answer&body=Yes%20I%20will%20be%20your%20valentine%20you%20sexy%20hunk%20of%20man%20❤️❤️❤️ :)"
                        }}
                    >
                    Yes
                    </button>
                    <button onClick={() => setStatusText("Aww...no...try again")}>No</button>
                </>}
            </div>
            <h3 className="status">{statusText}</h3>
        </div>
    )
}
